import React, { FC } from "react";
import { Fade } from "react-awesome-reveal";
import classNames from "classnames";
import { GatsbyImage } from "gatsby-plugin-image";
import { getGatsbyImage } from "gatsby-plugin-storyblok-image";
import { checkFileType, FileType } from "@app/helpers/check-file-type";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { IIconListItem, RichText } from "../../models";
import {
  BlockHeader,
  HeaderLevel,
} from "../../../common/block-header/BlockHeader";
import { BlockBody } from "../../../common/block-body/BlockBody";

type Props = {
  title: RichText;
  items: IIconListItem[];
};

export const IconList: FC<Props> = ({ title, items }) => (
  <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
    <div
      className={classNames(
        "mt-20 flex flex-col gap-y-20 px-5",
        "lg:mt-25 lg:flex-row lg:justify-between lg:px-16",
        "xl:mx-auto xl:mt-40 xl:max-w-container-wide xl:px-24",
      )}
    >
      <div className={classNames("lg:basis-[25%]", "xxl:basis-[22.5%]")}>
        <BlockHeader
          richText={title}
          headerLevel={HeaderLevel.H2}
          className={classNames(
            "text-medium text-primary-content",
            "lg:text-large",
            "xl:text-xx-large",
          )}
        />
      </div>
      <div className={classNames("lg:basis-[64%]", "xxl:basis-[65.5%]")}>
        <div
          className={classNames(
            "flex flex-col gap-y-15",
            "md:flex-row md:flex-wrap md:justify-between",
            "xxl:max-w-[87%]",
          )}
        >
          {items.map((item) => (
            <div
              key={item._uid}
              className={classNames(
                "text-regular text-base-400",
                "md:basis-[45%]",
                "lg:basis-[39.5%]",
              )}
            >
              <div className="mb-5 flex h-9 w-9 flex-col justify-center">
                {item.icon?.filename ? (
                  checkFileType(item.icon?.filename) === FileType.IMAGE ? (
                    <GatsbyImage
                      image={getGatsbyImage(item.icon.filename, {
                        layout: "fixed",
                      })}
                      alt={item.icon.alt}
                    />
                  ) : (
                    <img
                      src={item.icon.filename}
                      alt={item.icon.alt ?? "Icon"}
                    />
                  )
                ) : null}
              </div>
              <BlockHeader
                richText={item.title}
                headerLevel={HeaderLevel.H3}
                className={classNames(
                  "mb-2.5 max-w-[86%] text-medium text-primary-content",
                  "lg:max-w-full lg:text-medium",
                )}
              />
              <BlockBody richText={item.text} />
            </div>
          ))}
        </div>
      </div>
    </div>
  </Fade>
);
